export type RelightParams = {
  light_direction: 'left' | 'right' | 'top' | 'back';
  light_intensity: 'mood' | 'bright' | 'medium' | 'strong';
  light_temperature: 'warm' | 'cool' | 'neutral' | 'sunset';
  light_type: 'softbox' | 'sunlight' | 'spotlight' | 'ambient';
  /**
   * Upstream image URL captured from incoming edges when running the tool.
   * Not user-editable; used for the relight image model to read directly (via modelUi mapping).
   */
  source_image_url?: string;
};

export const DEFAULT_RELIGHT_PARAMS: RelightParams = {
  light_direction: 'left',
  light_intensity: 'medium',
  light_temperature: 'neutral',
  light_type: 'softbox',
};

const asString = (v: unknown) => (typeof v === 'string' ? v : undefined);
const isOneOf = <T extends readonly string[]>(
  v: unknown,
  allowed: T
): v is T[number] => typeof v === 'string' && (allowed as readonly string[]).includes(v);

export const normalizeRelightParams = (
  raw: Record<string, unknown> | undefined
): { params: RelightParams; needsHydrate: boolean } => {
  const allowedDirection = ['left', 'right', 'top', 'back'] as const;
  const allowedIntensity = ['mood', 'bright', 'medium', 'strong'] as const;
  const allowedTemperature = ['warm', 'cool', 'neutral', 'sunset'] as const;
  const allowedType = ['softbox', 'sunlight', 'spotlight', 'ambient'] as const;

  const r = raw ?? {};

  // Back-compat: map old camelCase keys -> new snake_case keys
  const migrated: Record<string, unknown> = { ...r };
  const oldDirection = asString((r as any)?.lightDirection);
  const oldIntensity = (r as any)?.lightIntensity;
  const oldType = asString((r as any)?.lightType);
  // old lightColor is not used anymore; map it to temperature if it's one of expected values
  const oldTemperature =
    asString((r as any)?.lightTemperature) ?? asString((r as any)?.lightColor);

  if (typeof migrated.light_direction === 'undefined' && oldDirection)
    migrated.light_direction = oldDirection;
  if (typeof migrated.light_intensity === 'undefined' && typeof oldIntensity !== 'undefined')
    migrated.light_intensity = String(oldIntensity);
  if (typeof migrated.light_temperature === 'undefined' && oldTemperature)
    migrated.light_temperature = oldTemperature;
  if (typeof migrated.light_type === 'undefined' && oldType) migrated.light_type = oldType;

  const params: RelightParams = {
    light_direction: isOneOf(migrated.light_direction, allowedDirection)
      ? migrated.light_direction
      : DEFAULT_RELIGHT_PARAMS.light_direction,
    light_intensity: isOneOf(migrated.light_intensity, allowedIntensity)
      ? migrated.light_intensity
      : DEFAULT_RELIGHT_PARAMS.light_intensity,
    light_temperature: isOneOf(migrated.light_temperature, allowedTemperature)
      ? migrated.light_temperature
      : DEFAULT_RELIGHT_PARAMS.light_temperature,
    light_type: isOneOf(migrated.light_type, allowedType)
      ? migrated.light_type
      : DEFAULT_RELIGHT_PARAMS.light_type,
    source_image_url: (() => {
      const direct = asString((migrated as any).source_image_url);
      if (direct && direct.trim()) return direct.trim();
      // Back-compat: if old param stored as image_urls[], pick first
      const arr = (migrated as any).image_urls;
      if (!Array.isArray(arr) || arr.length === 0) return undefined;
      const first = arr.find((x: any) => typeof x === 'string' && String(x).trim());
      return typeof first === 'string' ? first.trim() : undefined;
    })(),
  };

  const needsHydrate =
    (r as any).light_direction !== params.light_direction ||
    (r as any).light_intensity !== params.light_intensity ||
    (r as any).light_temperature !== params.light_temperature ||
    (r as any).light_type !== params.light_type ||
    // Back-compat keys still present
    typeof (r as any).lightDirection !== 'undefined' ||
    typeof (r as any).lightIntensity !== 'undefined' ||
    typeof (r as any).lightColor !== 'undefined' ||
    typeof (r as any).lightType !== 'undefined' ||
    // Keep upstream image stable
    String((r as any).source_image_url ?? '') !== String(params.source_image_url ?? '') ||
    typeof (r as any).image_urls !== 'undefined';

  return { params, needsHydrate };
};


